<template>
  <div class="js-timeline-component">
    <component
      :is="stepComponentName(name)"
      v-for="(step, name) in visibleSteps"
      :ref="`step-${step.id}`"
      :key="name"
      :step="step"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import kebabCase from 'lodash/kebabCase';
import { mapGetters, mapMutations, mapState } from 'vuex';
import { Bus } from '../../bus';

export default Vue.extend({
  name: 'CampaignTimeline',
  data: () => ({
    loading: false,
    campaignRequirementsOpen: false,
  }),
  props: ['campaignId'],
  computed: {
    ...mapState('timeline', ['creator', 'campaign', 'campaignApplication', 'order', 'steps', 'youtubeLink']),
    ...mapGetters('timeline', [
      'joined',
      'applied',
      'approved',
      'posted',
      'hasApprovedContent',
      'campaignShouldDeliver',
      'campaignHasCreativeAssetsObjective',
      'isPaidCampaign',
      'isPendingContract',
      'hasInstagramDeliverables',
    ]),
    visibleSteps() {
      const visibleSteps = {};

      Object.keys(this.steps).forEach((name) => {
        if (this.steps[name].visible) {
          visibleSteps[name] = this.steps[name];
        }
      });

      return visibleSteps;
    },
  },
  async created() {
    await this.getState();
    this.checkState();
  },
  mounted() {
    Bus.$on('joinedCampaign', this.onJoinCampaign);
    Bus.$on('onJoinCampaign', this.onJoinCampaign);
  },
  destroyed() {
    Bus.$off('joinedCampaign');
    Bus.$off('onJoinCampaign');
  },
  methods: {
    ...mapMutations('timeline', [
      'setCreator',
      'setCampaign',
      'setCampaignApplication',
      'setUploadedContent',
      'setOrder',
      'setCampaignOrderGuidelines',
      'setPhotosWeWant',
      'toggleStep',
      'setYoutubeLink',
      'setPostedDeliverables',
      'setFailedDeliverables',
      'setIsEligibleForCampaign',
      'setContentRejectionsCount',
      'markStepCompleted',
      'markStepActive',
      'markStepHidden',
    ]),
    stepComponentName(name) {
      return `${kebabCase(name)}-step`;
    },
    openStep(state) {
      state.open = false;
    },
    activateStep(state) {
      state.active = true;
      state.open = true;
      state.visible = true;
    },
    hideStep(state) {
      state.active = false;
      state.open = false;
      state.visible = false;
    },
    checkState() {
      const statusSteps = {
        no_application: [
          {
            stepName: 'briefAndJoin',
            visible: true,
            open: true,
            completed: false,
            active: true,
          },
          {
            stepName: 'placeOrder',
            visible: true,
            open: false,
            completed: false,
            active: false,
          },
          {
            stepName: 'postContent',
            visible: true,
            open: false,
            completed: false,
            active: false,
          },
          {
            stepName: 'uploadContent',
            visible: true,
            open: false,
            completed: false,
            active: false,
          },
        ],
        pending_approval: [
          {
            stepName: 'briefAndJoin',
            visible: true,
            open: true,
            completed: false,
            active: true,
          },
          {
            stepName: 'placeOrder',
            visible: true,
            open: false,
            completed: false,
            active: false,
          },
          {
            stepName: 'postContent',
            visible: true,
            open: false,
            completed: false,
            active: false,
          },
          {
            stepName: 'uploadContent',
            visible: true,
            open: false,
            completed: false,
            active: false,
          },
        ],
        pending_contract: [
          {
            stepName: 'briefAndJoin',
            visible: true,
            open: true,
            completed: false,
            active: true,
          },
          {
            stepName: 'placeOrder',
            visible: true,
            open: false,
            completed: false,
            active: false,
          },
          {
            stepName: 'postContent',
            visible: true,
            open: false,
            completed: false,
            active: false,
          },
          {
            stepName: 'uploadContent',
            visible: true,
            open: false,
            completed: false,
            active: false,
          },
        ],
        negotiating: [
          {
            stepName: 'briefAndJoin',
            visible: true,
            open: true,
            completed: false,
            active: true,
          },
          {
            stepName: 'placeOrder',
            visible: true,
            open: false,
            completed: false,
            active: false,
          },
          {
            stepName: 'postContent',
            visible: true,
            open: false,
            completed: false,
            active: false,
          },
          {
            stepName: 'uploadContent',
            visible: true,
            open: false,
            completed: false,
            active: false,
          },
        ],
        rejected_approval: [
          {
            stepName: 'briefAndJoin',
            visible: true,
            open: true,
            completed: false,
            active: false,
          },
          {
            stepName: 'placeOrder',
            visible: true,
            open: false,
            completed: false,
            active: false,
          },
          {
            stepName: 'postContent',
            visible: true,
            open: false,
            completed: false,
            active: false,
          },
          {
            stepName: 'uploadContent',
            visible: true,
            open: false,
            completed: false,
            active: false,
          },
        ],
        waiting_for_content: [
          {
            stepName: 'briefAndJoin',
            visible: true,
            open: false,
            completed: true,
            active: false,
          },
          {
            stepName: 'placeOrder',
            visible: true,
            open: true,
            completed: false,
            active: true,
          },
          {
            stepName: 'postContent',
            visible: true,
            open: false,
            completed: false,
            active: false,
          },
          {
            stepName: 'uploadContent',
            visible: true,
            open: false,
            completed: false,
            active: false,
          },
        ],
        review_content: [
          {
            stepName: 'briefAndJoin',
            visible: true,
            open: false,
            completed: true,
            active: false,
          },
          {
            stepName: 'placeOrder',
            visible: true,
            open: false,
            completed: true,
            active: false,
          },
          {
            stepName: 'postContent',
            visible: true,
            open: false,
            completed: false,
            active: false,
          },
          {
            stepName: 'uploadContent',
            visible: true,
            open: true,
            completed: false,
            active: true,
          },
        ],
        publish_content: [
          {
            stepName: 'briefAndJoin',
            visible: true,
            open: false,
            completed: true,
            active: false,
          },
          {
            stepName: 'placeOrder',
            visible: true,
            open: false,
            completed: true,
            active: false,
          },
          {
            stepName: 'uploadContent',
            visible: true,
            open: false,
            completed: true,
            active: false,
          },
          {
            stepName: 'postContent',
            visible: true,
            open: true,
            completed: false,
            active: true,
          },
        ],
        pending_payment: [
          {
            stepName: 'briefAndJoin',
            visible: true,
            open: false,
            completed: true,
            active: false,
          },
          {
            stepName: 'placeOrder',
            visible: true,
            open: false,
            completed: true,
            active: false,
          },
          {
            stepName: 'uploadContent',
            visible: true,
            open: true,
            completed: true,
            active: false,
          },
          {
            stepName: 'postContent',
            visible: true,
            open: false,
            completed: true,
            active: false,
          },
          {
            stepName: 'joinAnotherCampaign',
            visible: true,
            open: true,
            completed: false,
            active: false,
          },
        ],
        completed: [
          {
            stepName: 'briefAndJoin',
            visible: true,
            open: false,
            completed: true,
            active: false,
          },
          {
            stepName: 'placeOrder',
            visible: true,
            open: false,
            completed: true,
            active: false,
          },
          {
            stepName: 'uploadContent',
            visible: true,
            open: false,
            completed: true,
            active: false,
          },
          {
            stepName: 'postContent',
            visible: true,
            open: false,
            completed: true,
            active: false,
          },
          {
            stepName: 'joinAnotherCampaign',
            visible: true,
            open: true,
            completed: false,
            active: false,
          },
        ],
        cancelled: [
          {
            stepName: 'briefAndJoin',
            visible: true,
            open: false,
            completed: true,
            active: false,
          },
          {
            stepName: 'placeOrder',
            visible: true,
            open: false,
            completed: false,
            active: false,
          },
          {
            stepName: 'postContent',
            visible: true,
            open: false,
            completed: false,
            active: false,
          },
        ],
      };

      if (this.campaignApplication) {
        statusSteps[this.campaignApplication?.status].forEach((step) => {
          this.steps[step.stepName] = { ...this.steps[step.stepName], ...step };

          if (this.approved) {
            if (this.order && this.order.id) {
              this.markStepCompleted(this.steps.placeOrder.id);

              if (!this.hasApprovedContent) {
                this.markStepActive(this.steps.uploadContent.id);
              }
            }

            if (
              this.campaign.reward_type !== 'gift_card' &&
              this.campaignApplication?.status === 'waiting_for_content'
            ) {
              this.markStepHidden(this.steps.placeOrder.id);
              this.markStepActive(this.steps.uploadContent.id);
            }
          }

          if (this.campaignHasCreativeAssetsObjective) {
            this.markStepHidden(this.steps.postContent.id);
          }
        });
      } else {
        statusSteps.no_application.forEach((step) => {
          this.steps[step.stepName] = { ...this.steps[step.stepName], ...step };
        });
      }
    },
    onJoinCampaign({ campaignApplication, campaignOrderGuidelines }) {
      this.setCampaignApplication(campaignApplication);
      this.setCampaignOrderGuidelines(campaignOrderGuidelines);
      this.checkState();

      // eslint-disable-next-line no-restricted-syntax
      for (const stepId of Object.keys(this.steps)) {
        if (Object.prototype.hasOwnProperty.call(this.steps, stepId)) {
          const step = this.steps[stepId];

          if (step.visible && !step.completed) {
            setTimeout(() => {
              this.scrollToStep(stepId);
            }, 300);
            break;
          }
        }
      }
    },
    scrollToStep(stepId) {
      this.$refs[`step-${stepId}`][0].$el.scrollIntoView(true);
    },
    onFilesSelected() {
      this.files = this.$refs.file.files;
    },
    async getState() {
      const { data } = await window.axios.get(`/campaigns/${this.campaignId}/timeline`);

      if (data && data.creator) {
        this.setOrder(data.order);
        this.setIsEligibleForCampaign(data.isEligibleForCampaign);
        this.setCreator(data.creator);
        this.setCampaign(data.campaign);
        this.setUploadedContent(data.uploadedContent);
        this.setPhotosWeWant(data.photosWeWant);
        this.setCampaignOrderGuidelines(data.campaignOrderGuidelines);
        this.setCampaignApplication(data.campaignApplication);
        this.setYoutubeLink(data.youtubeLink);
        this.setPostedDeliverables(data.postedDeliverables);
        this.setFailedDeliverables(data.failedDeliverables);
        this.setContentRejectionsCount(data.contentRejectionsCount);
      }
    },
  },
});
</script>

<style>
.dropzone {
  min-height: 0;
  padding: 1rem;
}
.dropzone .dz-message {
  margin-top: 0;
  margin-bottom: 0;
}

.campaign-requirements-preview::before {
  position: absolute;
  content: '';
  width: 100%;
  height: 80px;
  top: auto;
  bottom: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #f7bbbb 100%);
}
.campaign-requirements-preview.campaign-requirements-preview-open::before {
  display: none;
}
</style>
